import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import type { IndexedArticle } from "@/lib/article-index.generated";

export function SearchDialog() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  // The article index is loaded on first open so it never ships in the
  // shared bundle that every page downloads.
  const [articles, setArticles] = useState<IndexedArticle[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || articles.length) return;
    let alive = true;
    import("@/lib/article-index.generated").then((m) => {
      if (alive) setArticles(m.ALL_ARTICLES);
    });
    return () => {
      alive = false;
    };
  }, [open, articles.length]);

  // Keyboard shortcut: ⌘K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else setQ("");
  }, [open]);

  const results: IndexedArticle[] = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return articles.slice(0, 8);
    return articles.filter((a: IndexedArticle) => {
      const hay = `${a.title} ${a.description} ${a.category} ${a.countryLabel}`.toLowerCase();
      return hay.includes(term);
    }).slice(0, 12);
  }, [q, articles]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search articles"
        title="Search articles (⌘K)"
        className="inline-flex h-9 items-center gap-2 rounded-full border border-navy/15 bg-card/70 px-3 text-xs font-medium text-foreground/70 transition hover:border-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <Search aria-hidden="true" className="h-3.5 w-3.5" />
        <span className="hidden lg:inline">Search…</span>
        <kbd className="hidden rounded border border-navy/10 bg-card px-1.5 py-0.5 text-[10px] font-semibold text-foreground/60 lg:inline">⌘K</kbd>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Search"
          className="fixed inset-0 z-[100] flex items-start justify-center bg-navy/40 p-4 backdrop-blur-sm sm:pt-24"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
            <div className="flex items-center gap-3 border-b border-border px-4 py-3">
              <Search aria-hidden="true" className="h-4 w-4 text-accent" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                maxLength={120}
                placeholder="Search visas, jobs, tax, countries…"
                className="min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close search"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            </div>
            <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto">
              {results.length === 0 && (
                <li className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No matches for "{q}"
                </li>
              )}
              {results.map((r) => (
                <li key={r.href}>
                  <Link title={`Open guide: ${r.title}`}
                    to={r.href}
                    onClick={() => setOpen(false)}
                    className="flex flex-col gap-1 px-4 py-3 transition hover:bg-muted/40"
                  >
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-accent">
                        {r.countryLabel}
                      </span>
                      <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                        {r.category}
                      </span>
                    </div>
                    <span className="line-clamp-1 text-sm font-semibold text-foreground">{r.title}</span>
                    <span className="line-clamp-1 text-xs text-muted-foreground">{r.description}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

export default SearchDialog;
